import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { SportsEsportsRounded } from "@mui/icons-material";
import { colors } from "../../theme/colors";
import { buildProfileMatches } from "./profileRecentMatches";
import ProfileMatchesTable from "./ProfileMatchesTable";
import { EmptyDataState } from "../common/EmptyDataState";
import { ProfileMatchRowsSkeleton } from "./profileSkeletons";

const MATCH_HISTORY_CARD_SX = {
    display: "flex",
    flexDirection: "column",
    borderRadius: "12px",
    position: "relative",
    background: `
        linear-gradient(
            180deg,
            rgba(12,12,14,0.96) 0%,
            rgba(8,8,10,0.985) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.05)",
    backdropFilter: "blur(18px)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.05),
        inset 0 -40px 80px rgba(246,185,76,0.03),
        0 24px 60px rgba(0,0,0,0.42)
    `,
    gap: "1rem",
    padding: "1.5rem",
    minWidth: 0,
};

const CARD_TOP_GLOW_SX = {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "42%",
    height: "1px",
    background: `
        linear-gradient(
            90deg,
            transparent,
            rgba(246,185,76,0.95),
            transparent
        )
    `,
    boxShadow: "0 0 18px rgba(246,185,76,0.45)",
    pointerEvents: "none",
};

export default function ProfileMatchHistoryTab({
    last25MatchData = {},
    totalPlayedMatches,
    isLoading = false,
}) {
    const matches = useMemo(
        () => buildProfileMatches(last25MatchData),
        [last25MatchData]
    );

    if (!isLoading && matches.length === 0) {
        return (
            <EmptyDataState
                sectionTitle="Match History"
                sectionSubtitle="Full competitive record"
                title="No match history available"
                icon={SportsEsportsRounded}
                minHeight="18rem"
            >
                This player has not played any matches yet
            </EmptyDataState>
        );
    }

    return (
        <Box sx={MATCH_HISTORY_CARD_SX}>
            <Box sx={CARD_TOP_GLOW_SX} />
            <Box sx={{ position: "relative", zIndex: 2 }}>
                <Typography
                    sx={{
                        fontSize: "0.8rem",
                        fontWeight: 800,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: colors.text.primary,
                        lineHeight: 1,
                        mb: "0.35rem",
                    }}
                >
                    Match History
                </Typography>
                {!isLoading ? (
                    <Typography
                        sx={{
                            fontSize: "0.725rem",
                            fontWeight: 500,
                            letterSpacing: "0.04em",
                            color: "rgba(255,255,255,0.42)",
                            mb: "1rem",
                        }}
                    >
                        {Number.isFinite(totalPlayedMatches)
                            ? totalPlayedMatches
                            : matches.length}{" "}
                        total matches
                    </Typography>
                ) : null}
                {isLoading ? (
                    <ProfileMatchRowsSkeleton count={8} />
                ) : (
                    <ProfileMatchesTable matches={matches} />
                )}
            </Box>
        </Box>
    );
}

ProfileMatchHistoryTab.propTypes = {
    last25MatchData: PropTypes.object,
    totalPlayedMatches: PropTypes.number,
    isLoading: PropTypes.bool,
};
